/**
 * RC-8: single right-rail drawer enforcement.
 *
 * Mora has historically had ~6 separate drawer/dialog stores
 * (useArtifactStore, useImageDialogStore, useDocumentDialogStore,
 * useStrategyDialogStore, useBrandGuidelinesDialogStore,
 * useVideoDialogStore). Each opens its own surface, and previously they
 * could stack on top of each other in the right rail — which the user
 * called out as visually broken (image opens behind script panel; closing
 * one reveals another at a different width).
 *
 * Contract: there is ONE right rail. Every open() routes through
 * `closeOtherDrawers(self)` first so the rail swaps content rather than
 * stacking. The Inspector still owns the actual presentation; the stores
 * are payload carriers.
 *
 * Each store's open() should call `closeOtherDrawers('storeName')` BEFORE
 * applying its own state mutation.
 */

export type DrawerOwner =
  | 'artifact'
  | 'image'
  | 'document'
  | 'strategy'
  | 'brandGuidelines'
  | 'video';

type CloseFn = () => void;

const closers: Partial<Record<DrawerOwner, CloseFn>> = {};

/** Each store registers its own close fn at module-init. */
export function registerDrawerCloser(owner: DrawerOwner, close: CloseFn): void {
  closers[owner] = close;
}

/** Call BEFORE opening — closes every OTHER drawer so the rail can swap. */
export function closeOtherDrawers(self: DrawerOwner): void {
  for (const [owner, close] of Object.entries(closers) as Array<[DrawerOwner, CloseFn]>) {
    if (owner === self) continue;
    try {
      close();
    } catch {
      /* swallow — never let one drawer's close break another's open */
    }
  }
}
